import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Gift, LogOut, Sprout, Wheat, Sparkles, Flame, Leaf, Wallet, TrendingUp, Bell, BellOff } from "lucide-react";
import { fetchRestaurant, loyaltyToken, loyaltyRegister, loyaltyLogin, loyaltyLogout, loyaltyMe, loyaltyPushTest, loyaltyMagicLogin, loyaltySetPassword, loyaltyListRewards, loyaltyRedeemReward } from "../lib/api";
import { loyaltyPushSupported, loyaltyPushStatus, loyaltyPushEnable, loyaltyPushDisable } from "../lib/loyaltyPush";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { SubPageNav } from "../components/SubPageNav";
import { RestaurantHeader } from "../components/RestaurantHeader";
import { QRDialog } from "../components/QRDialog";
import AppBottomBar from "../components/AppBottomBar";
import { Toaster } from "../components/ui/toaster";
import { toast } from "../hooks/use-toast";
import PozzoTopNav from "../components/PozzoTopNav";
import { T, G, tr } from "../lib/i18n";
import { ReferralCard, REF_KEY } from "../components/loyalty/Referral";
import { MyDataCard } from "../components/loyalty/MyDataCard";
import { ConsentNote, MarketingOptIn, LegalLinks } from "../components/legal/LegalBits";

// Icons for the four cashback tiers (agricultural theme).
const LEVEL_ICONS = {
  seme:      Leaf,     // germoglio in erba
  germoglio: Sprout,
  spiga:     Wheat,
  raccolta:  Flame,    // "raccolta" = full harvest
};
const LEVEL_COLORS = {
  seme:      "text-amber-700",
  germoglio: "text-emerald-600",
  spiga:     "text-yellow-700",
  raccolta:  "text-[#4a1c17]",
};

function fmtEuro(cents) {
  const v = Number(cents || 0) / 100;
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
}

// Human-friendly label for a transaction type.
function txnLabel(t, lang) {
  const type = (t?.type || "").toLowerCase();
  if (type === "welcome") return { label: "Benvenuto", cls: "text-amber-800 bg-amber-50 border-amber-200" };
  if (type === "earn")    return { label: `Spesa · +${t.cashback_pct || 0}%`, cls: "text-emerald-800 bg-emerald-50 border-emerald-200" };
  if (type === "redeem")  return { label: "Utilizzo credito", cls: "text-rose-800 bg-rose-50 border-rose-200" };
  if (type === "credit")  return { label: "Credito ricevuto", cls: "text-[#4a1c17] bg-[#fdf6e9] border-[#C48B31]/50" };
  return { label: type || "Movimento", cls: "text-neutral-700 bg-neutral-50 border-neutral-200" };
}

export default function LoyaltyPage({ showSubNav = false }) {
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(Boolean(loyaltyToken()));
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [pushStatus, setPushStatus] = useState(() => loyaltyPushStatus());
  const [pushBusy, setPushBusy] = useState(false);
  // Exclusive experiences catalogue
  const [rewards, setRewards] = useState([]);
  const [redeemingId, setRedeemingId] = useState(null);
  // Magic-link one-click login flow (see backend /api/loyalty/magic-login).
  // If the URL carries `?magic=<token>` we auto-login, then optionally
  // prompt the customer to choose a password (they can also skip).
  const [magicPasswordPrompt, setMagicPasswordPrompt] = useState(false);
  const [magicPwd, setMagicPwd] = useState("");
  const [magicSaving, setMagicSaving] = useState(false);

  useEffect(() => { localStorage.setItem("lang", lang); }, [lang]);
  useEffect(() => { if (showSubNav) fetchRestaurant().then(setRestaurant).catch(() => {}); }, [showSubNav]);

  // Handle magic-link landing (?magic=<token>) once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get("magic");
    if (!tok) return;
    (async () => {
      try {
        const r = await loyaltyMagicLogin(tok);
        localStorage.setItem("loyalty_token", r.token);
        setAuthed(true);
        if (r.needs_password) setMagicPasswordPrompt(true);
        // Clean the URL so the token doesn't stay in history.
        const clean = window.location.pathname;
        window.history.replaceState({}, "", clean);
        await load();
      } catch (err) {
        toast({ title: tr(G.invalidLink, lang), description: err?.response?.data?.detail || tr(G.linkExpired, lang) });
        const clean = window.location.pathname;
        window.history.replaceState({}, "", clean);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveMagicPassword = async () => {
    if ((magicPwd || "").length < 6) {
      toast({ title: tr(G.pwdShort, lang), description: tr(G.min6, lang) });
      return;
    }
    setMagicSaving(true);
    try {
      await loyaltySetPassword(magicPwd);
      toast({ title: tr(G.pwdSet, lang), description: tr(G.pwdSetDesc, lang) });
      setMagicPasswordPrompt(false);
      setMagicPwd("");
    } catch (err) {
      toast({ title: tr(G.error, lang), description: err?.response?.data?.detail || err?.message });
    } finally { setMagicSaving(false); }
  };

  const load = async () => {
    if (!loyaltyToken()) { setAuthed(false); return; }
    setLoading(true);
    try { const d = await loyaltyMe(); setData(d); setAuthed(true); }
    catch (e) {
      if (e?.response?.status === 401) { loyaltyLogout(); setAuthed(false); }
      else toast({ title: tr(G.error, lang), description: e?.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loyaltyListRewards().then(setRewards).catch(() => {}); }, []);

  // Friend invite: `?ref=CODE` is kept until registration; `#invita` scrolls to the invite card (or opens sign-up).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { localStorage.setItem(REF_KEY, ref.toUpperCase()); setMode("register"); }
    if (window.location.hash === "#invita" && !loyaltyToken()) setMode("register");
  }, []);
  useEffect(() => {
    if (window.location.hash !== "#invita" || !authed || loading) return;
    const t = setTimeout(() => document.getElementById("invita")?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    return () => clearTimeout(t);
  }, [authed, loading]);
  const refCode = localStorage.getItem(REF_KEY);

  const requestReward = async (r) => {
    if (!window.confirm(`Richiedere l'esperienza "${r.name_it}"?\n\nRiceverai un messaggio dalla struttura per organizzarla insieme.`)) return;
    setRedeemingId(r.id);
    try {
      await loyaltyRedeemReward(r.id);
      toast({ title: tr(G.requestSent, lang), description: tr(G.requestSentDesc, lang) });
      await load();
    } catch (e) {
      toast({ title: tr(G.error, lang), description: e?.response?.data?.detail || e?.message });
    } finally { setRedeemingId(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const r = await loyaltyRegister({ ...form, ref: refCode || undefined, marketing });
        localStorage.removeItem(REF_KEY);
        if (r?.invited_by) toast({ title: `🎁 Benvenuto! ${r.invited_by} ti ha invitato`, description: "Hai già 10€ di credito pronti da usare alla tua prima visita. Ti abbiamo mandato anche un'email di benvenuto." });
      }
      else await loyaltyLogin({ email: form.email, password: form.password });
      await load();
    } catch (err) {
      toast({ title: tr(G.error, lang), description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  const doLogout = () => { loyaltyLogout(); setAuthed(false); setData(null); };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushStatus === "granted") {
        await loyaltyPushDisable();
        setPushStatus("default");
        toast({ title: tr(G.notifOff, lang) });
      } else {
        await loyaltyPushEnable();
        setPushStatus("granted");
        try { await loyaltyPushTest(); } catch (_) { /* silent */ }
        toast({ title: tr(G.notifOn, lang), description: tr(G.notifOnDesc, lang) });
      }
    } catch (err) {
      toast({ title: tr(G.notifError, lang), description: err?.message || String(err) });
    } finally { setPushBusy(false); }
  };

  const user = data?.user;
  const balance = user?.balance_cents || 0;
  const lifetime = user?.lifetime_spent_cents || 0;
  const level = user?.level;
  const nextLv = user?.next_level;

  // Progress from current tier threshold → next tier threshold.
  const curMin = level?.min_spent_cents || 0;
  const nextMin = nextLv?.min_spent_cents || Math.max(curMin + 1, lifetime + 1);
  const progressPct = nextLv
    ? Math.min(100, Math.max(0, ((lifetime - curMin) / (nextMin - curMin)) * 100))
    : 100;
  const missingCents = nextLv ? Math.max(0, nextMin - lifetime) : 0;

  const LevelIcon = level ? (LEVEL_ICONS[level.key] || Sprout) : Sprout;
  const levelColor = level ? (LEVEL_COLORS[level.key] || "text-[#7c5a4e]") : "text-[#7c5a4e]";

  return (
    <div className="min-h-screen bg-[#faf3ee]">
      {showSubNav ? (
        <RestaurantHeader restaurant={restaurant} lang={lang} setLang={setLang} hideCover compact stickyBar onOpenQR={() => setQrOpen(true)} />
      ) : (
        <PozzoTopNav lang={lang} setLang={setLang} />
      )}
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="loyalty-page">
        {showSubNav && <SubPageNav hideMenu lang={lang} />}

        {/* Hero standalone rounded card with looping video, matching /spa /cavallo /bici layout */}
        <section className="relative overflow-hidden rounded-3xl bg-[#2b1410] text-[#f7ebdf] shadow-lg min-h-[320px] sm:min-h-[440px] flex items-center" data-testid="loyalty-hero">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/media/hero-fidelity.mp4"
            poster="/media/hero-fidelity-poster.jpg"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2b1410]/95 via-[#2b1410]/55 to-[#2b1410]/25 pointer-events-none" />
          <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none"
               style={{ backgroundImage: "radial-gradient(rgba(255,220,180,0.4) 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

          <div className="relative w-full px-6 py-10 sm:px-12 sm:py-14 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/25 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-[#e6c46a]">
              <Gift className="h-3.5 w-3.5" /> {tr(T.loyaltyProgram, lang)}
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-6xl font-bold leading-[0.95] uppercase text-[#f7ebdf] drop-shadow-lg">
              {tr(T.loyaltyName, lang)}
            </h1>
            <p className="mt-5 max-w-md mx-auto text-sm sm:text-base text-[#f7ebdf]/95 drop-shadow">
              Credito in € ad ogni visita: ristorante, spa, massaggi, camere. Sali di livello e sblocca cashback fino al <span className="text-[#e6c46a] font-semibold">10%</span>.
            </p>
          </div>
        </section>

        <div className="mt-6 rounded-2xl overflow-hidden border border-[#e8dcc8] shadow-sm bg-white">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4a1c17]" /></div>
          ) : !authed ? (
            <form onSubmit={submit} className="p-6 sm:p-8 space-y-4 bg-white" data-testid="loyalty-auth-form">
              <div className="flex rounded-full bg-[#faf3ee] p-1">
                <button type="button" onClick={() => setMode("login")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold ${mode === "login" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}>{tr(T.loyaltyLogin, lang)}</button>
                <button type="button" onClick={() => setMode("register")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold ${mode === "register" ? "bg-[#4a1c17] text-white" : "text-[#4a1c17]"}`}>{tr(T.loyaltyRegister, lang)}</button>
              </div>
              {mode === "register" && (
                <>
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
                    <Gift className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>Ti regaliamo <strong>10€ di credito</strong> di benvenuto appena completi l'iscrizione.{refCode && <> Sei stato invitato da un amico (codice <strong data-testid="loyalty-ref-badge">{refCode}</strong>): alla tua prima visita anche lui riceve 5€.</>}</span>
                  </div>
                  <input type="text" placeholder="NOME E COGNOME" required minLength={2}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                    data-testid="loyalty-name"
                    autoComplete="name"
                    style={{ textTransform: "uppercase" }}
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 bg-white uppercase tracking-wide" />
                  <input type="tel" placeholder={tr(G.phonePh, lang)} value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    onBlur={(e) => {
                      const raw = e.target.value || "";
                      const digits = raw.replace(/[^\d+]/g, "");
                      let out = digits;
                      if (digits && !digits.startsWith("+")) {
                        if (digits.startsWith("00")) out = "+" + digits.slice(2);
                        else if (digits.startsWith("3") && digits.length >= 9) out = "+39" + digits;
                        else out = "+" + digits;
                      }
                      setForm({ ...form, phone: out });
                    }}
                    inputMode="tel"
                    autoComplete="tel"
                    className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 bg-white" />
                </>
              )}
              <input type="email" placeholder="email@esempio.com" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase().trim() })}
                autoComplete="email"
                data-testid="loyalty-email" className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 bg-white lowercase" />
              <input type="password" placeholder="Password (min 6)" required minLength={6}
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="loyalty-password" className="w-full rounded-lg border border-[#e8dcc8] px-3 py-2.5 bg-white" />
              {mode === "register" && <MarketingOptIn checked={marketing} onChange={setMarketing} testId="loyalty-marketing" />}
              <button type="submit" disabled={busy} data-testid="loyalty-submit"
                className="w-full rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-5 py-3 font-semibold disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : (mode === "register" ? tr(T.loyaltyCreate, lang) : tr(T.loyaltyLogin, lang))}
              </button>
              <ConsentNote action={mode === "register" ? "Iscrivendoti" : "Accedendo"} />
            </form>
          ) : (
            <div className="p-5 sm:p-6 bg-white space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#7c5a4e] uppercase tracking-wide">{tr(T.loyaltyHi, lang)}</div>
                  <div className="font-serif text-xl font-bold text-[#2b1410] truncate">{user?.name}</div>
                </div>
                <button onClick={doLogout} className="text-xs text-[#7c5a4e] inline-flex items-center gap-1 hover:underline" data-testid="loyalty-logout">
                  <LogOut className="h-3.5 w-3.5" /> {tr(T.loyaltyExit, lang)}
                </button>
              </div>

              {/* Balance hero */}
              <div className="rounded-2xl bg-gradient-to-br from-[#4a1c17] to-[#2b1410] text-[#f7ebdf] p-5 sm:p-6" data-testid="loyalty-balance-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest opacity-75 flex items-center gap-1.5">
                      <Wallet className="h-3.5 w-3.5" /> Il tuo credito
                    </div>
                    <div className="font-serif text-4xl sm:text-5xl font-bold mt-1" data-testid="loyalty-balance">
                      {fmtEuro(balance)}
                    </div>
                    <div className="text-xs opacity-80 mt-1">
                      Disponibile subito · usalo alla prossima visita
                    </div>
                  </div>
                  <div className={`h-14 w-14 rounded-full bg-white/10 flex items-center justify-center ${levelColor.replace("text-", "text-[#f7ebdf] ").split(" ").slice(-1)[0]}`}>
                    <LevelIcon className="h-7 w-7 text-[#f7ebdf]" />
                  </div>
                </div>
              </div>

              {/* Level + progress */}
              <div className="rounded-xl bg-[#faf3ee] border border-[#e8dcc8] p-4" data-testid="loyalty-level-card">
                <div className="flex items-center gap-3">
                  <LevelIcon className={`h-8 w-8 ${levelColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e]">{tr(T.loyaltyLevel, lang)} attuale</div>
                    <div className="font-serif text-xl font-bold text-[#2b1410]">
                      {tr({ it: level?.label_it, en: level?.label_en }, lang) || level?.label_it || "Seme"}
                    </div>
                    <div className="text-xs text-[#7c5a4e]">
                      {tr({ it: level?.benefit_it, en: level?.benefit_en }, lang) || level?.benefit_it || "3% di credito su ogni spesa"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-[#7c5a4e]">Cashback</div>
                    <div className="font-serif text-2xl font-bold text-[#4a1c17]">{level?.cashback_pct || 3}%</div>
                  </div>
                </div>
                {nextLv ? (
                  <div className="mt-3">
                    <div className="flex justify-between text-[11px] text-[#7c5a4e]">
                      <span>
                        Prossimo: <strong className="text-[#2b1410]">
                          {tr({ it: nextLv.label_it, en: nextLv.label_en }, lang) || nextLv.label_it}
                        </strong> ({nextLv.cashback_pct}%)
                      </span>
                      <span>{fmtEuro(lifetime)} / {fmtEuro(nextMin)}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-[#e8dcc8] overflow-hidden">
                      <div className="h-full bg-[#4a1c17]" style={{ width: `${progressPct}%` }} data-testid="loyalty-progress-bar" />
                    </div>
                    <div className="mt-1 text-[11px] text-center text-[#4a1c17] font-semibold">
                      Ti mancano <span className="tabular-nums">{fmtEuro(missingCents)}</span> di spesa per il prossimo livello
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-[11px] text-center text-emerald-700 font-semibold">
                    🌾 Livello massimo raggiunto — grazie per la fedeltà!
                  </div>
                )}
              </div>

              <ReferralCard lang={lang} />

              {/* All tiers overview */}
              <div>
                <div className="text-xs uppercase tracking-wider text-[#7c5a4e] mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3.5 w-3.5" /> Livelli & cashback
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="loyalty-tiers-overview">
                  {(data?.levels || []).map((lv) => {
                    const Icon = LEVEL_ICONS[lv.key] || Sprout;
                    const isCurrent = level?.key === lv.key;
                    return (
                      <div key={lv.key}
                        className={`rounded-xl border p-3 text-center ${isCurrent ? "bg-[#4a1c17] text-[#f7ebdf] border-[#4a1c17]" : "bg-white border-[#e8dcc8] text-[#2b1410]"}`}>
                        <Icon className={`h-5 w-5 mx-auto ${isCurrent ? "text-[#f7ebdf]" : LEVEL_COLORS[lv.key] || "text-[#7c5a4e]"}`} />
                        <div className={`font-serif font-bold text-sm mt-1 ${isCurrent ? "" : ""}`}>{lv.label_it}</div>
                        <div className={`text-[11px] ${isCurrent ? "opacity-90" : "text-[#7c5a4e]"}`}>{lv.cashback_pct}% cashback</div>
                        <div className={`text-[10px] mt-1 ${isCurrent ? "opacity-75" : "text-[#7c5a4e]"}`}>da {fmtEuro(lv.min_spent_cents)} spesi</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Exclusive experiences unlocked by fidelity level */}
              {rewards.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#7c5a4e] mb-2 flex items-center gap-1">
                    <Gift className="h-3.5 w-3.5" /> Esperienze esclusive
                  </div>
                  <div className="space-y-2" data-testid="loyalty-rewards-list">
                    {rewards.map((r) => {
                      const levelsList = data?.levels || [];
                      const rank = (k) => levelsList.findIndex((l) => l.key === k);
                      const unlocked = rank(level?.key || "seme") >= rank(r.min_level_key || "germoglio");
                      const reqLv = levelsList.find((l) => l.key === (r.min_level_key || "germoglio"));
                      const alreadyPending = (data?.redemptions || []).some((x) => x.reward_id === r.id && x.status === "pending");
                      return (
                        <div key={r.id} className={`rounded-xl border p-3 flex items-center gap-3 ${unlocked ? "bg-white border-[#4a1c17]/30" : "bg-[#f0e5d0]/40 border-[#e8dcc8] opacity-80"}`}>
                          <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${unlocked ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-[#e8dcc8] text-[#7c5a4e]"}`}>
                            <Gift className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-serif font-bold text-sm text-[#2b1410]">{r.name_it}</div>
                            {r.description_it && <div className="text-[11px] text-[#7c5a4e] mt-0.5">{r.description_it}</div>}
                            <div className={`text-[10px] mt-1 font-semibold uppercase tracking-wider ${unlocked ? "text-emerald-700" : "text-[#7c5a4e]"}`}>
                              {unlocked ? "✓ Sbloccato" : `Sblocca a: ${reqLv?.label_it || r.min_level_key}`}
                            </div>
                          </div>
                          {unlocked && (
                            alreadyPending ? (
                              <span className="text-[11px] font-semibold text-amber-700 whitespace-nowrap">In lavorazione</span>
                            ) : (
                              <button
                                onClick={() => requestReward(r)}
                                disabled={redeemingId === r.id}
                                data-testid={`loyalty-reward-request-${r.id}`}
                                className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-semibold whitespace-nowrap disabled:opacity-60"
                              >
                                {redeemingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Richiedi"}
                              </button>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Push notifications toggle */}
              {loyaltyPushSupported() && (
                <button
                  type="button"
                  onClick={togglePush}
                  disabled={pushBusy || pushStatus === "denied"}
                  data-testid="loyalty-push-toggle"
                  className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium shadow-sm transition-shadow hover:shadow disabled:opacity-60 ${
                    pushStatus === "granted"
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : pushStatus === "denied"
                      ? "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                      : "bg-[#4a1c17] hover:bg-[#2b1410] text-white"
                  }`}
                >
                  {pushBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : pushStatus === "granted" ? (
                    <BellOff className="h-4 w-4" />
                  ) : (
                    <Bell className="h-4 w-4" />
                  )}
                  {pushStatus === "granted"
                    ? "Notifiche cashback attive · disattiva"
                    : pushStatus === "denied"
                    ? "Notifiche bloccate dal browser"
                    : "🔔 Attiva notifiche cashback"}
                </button>
              )}

              {/* Google Wallet - add to phone */}
              {user?.id && (
                <a
                  href={`${API}/loyalty/${user.id}/wallet-link`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="add-to-google-wallet-btn"
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const res = await fetch(`${API}/loyalty/${user.id}/wallet-link`);
                      const d = await res.json();
                      if (d.url) window.location.href = d.url;
                      else toast({ title: tr(G.error, lang), description: d.detail || tr(G.walletUnavailable, lang) });
                    } catch (err) { toast({ title: tr(G.error, lang), description: err.message }); }
                  }}
                  className="w-full flex items-center justify-center gap-2 rounded-xl bg-black text-white hover:bg-neutral-800 px-4 py-3 text-sm font-medium shadow-sm transition-shadow hover:shadow"
                >
                  <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                    <rect x="6" y="12" width="36" height="24" rx="3" fill="#4285F4"/>
                    <rect x="6" y="12" width="36" height="8" fill="#FBBC04"/>
                    <circle cx="34" cy="26" r="3" fill="white"/>
                  </svg>
                  Aggiungi tessera a Google Wallet
                </a>
              )}

              {/* Transactions log */}
              {(user?.transactions || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#7c5a4e] mb-2 flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" /> Ultimi movimenti
                  </div>
                  <div className="space-y-1.5" data-testid="loyalty-transactions">
                    {[...user.transactions].reverse().map((t, i) => {
                      const { label, cls } = txnLabel(t, lang);
                      const gained = t.cashback_cents || t.earned_cents || 0;
                      const spent = t.redeemed_cents || 0;
                      const amountSpent = t.amount_spent_cents || 0;
                      return (
                        <div key={i} className={`rounded-lg border px-3 py-2 flex items-center gap-3 ${cls}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold">{label}</div>
                            <div className="text-[11px] opacity-80 truncate">
                              {t.note || ""}{amountSpent ? ` · spesa ${fmtEuro(amountSpent)}` : ""} · {fmtDate(t.ts)}
                            </div>
                          </div>
                          <div className="font-mono font-bold text-sm shrink-0">
                            {spent ? `−${fmtEuro(spent)}` : `+${fmtEuro(gained)}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-[#faf3ee] border border-dashed border-[#e8dcc8] p-3 text-[11px] text-[#7c5a4e]">
                💡 Il credito si aggiorna in reception ad ogni tua visita. Puoi usarlo come sconto sul conto in qualsiasi momento — basta mostrare la tessera Wallet o dire nome e cognome.
              </div>

              <MyDataCard onDeleted={doLogout} />
            </div>
          )}
        </div>
        {!authed && <LegalLinks className="mt-6" />}
      </main>
      <AppBottomBar current="fidelity" lang={lang} spacer={!showSubNav} />
      <QRDialog open={qrOpen} onOpenChange={setQrOpen} lang={lang} restaurant={restaurant} />
      <Toaster />

      {magicPasswordPrompt && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="magic-password-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-[#4a1c17] text-white px-5 py-4">
              <div className="font-serif text-xl font-bold">🌿 Benvenuto sulla tua tessera!</div>
              <div className="text-xs opacity-90 mt-1">Puoi già usare la tessera così com'è (Google Wallet, cashback, push). Se vuoi accedere anche da altri dispositivi via web, scegli una password.</div>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-sm font-semibold text-[#2b1410]">Password (opzionale, min 6 caratteri)</label>
              <input
                type="password"
                value={magicPwd}
                onChange={(e) => setMagicPwd(e.target.value)}
                placeholder="La tua password"
                className="w-full border border-[#e8dcc8] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#4a1c17]"
                data-testid="magic-password-input"
                autoFocus
              />
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => { setMagicPasswordPrompt(false); setMagicPwd(""); }}
                  className="flex-1 rounded-xl border border-[#e8dcc8] px-4 py-2.5 text-sm font-semibold text-[#7c5a4e] hover:bg-[#faf3ee]"
                  data-testid="magic-skip-btn"
                >
                  Salta, uso solo Wallet
                </button>
                <button
                  onClick={saveMagicPassword}
                  disabled={magicSaving || magicPwd.length < 6}
                  className="flex-1 rounded-xl bg-[#4a1c17] hover:bg-[#2b1410] disabled:opacity-50 px-4 py-2.5 text-sm font-semibold text-white"
                  data-testid="magic-save-btn"
                >
                  {magicSaving ? tr(G.saving, lang) : tr(G.savePassword, lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
